import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { CartUpdateEvent } from '@theme/events';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // Product/cart changes should force a fresh fetch for drawer upsells
      if (mutation.attributeName === 'data-product-id' && this.dataset.cartUpsell === 'true') {
        this.dataset.recommendationsPerformed = 'false';
        this.#cachedRecommendations = {};
      }

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Abort controller for cart event listeners
   * @type {AbortController | null}
   */
  #cartEventsAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#mutationObserver.observe(this, { attributes: true });

    // Cart drawer upsell: load immediately (closed dialogs often never intersect)
    if (this.dataset.cartUpsell === 'true') {
      this.#cartEventsAbortController = new AbortController();
      document.addEventListener(CartUpdateEvent.eventName, this.#handleCartChange, {
        signal: this.#cartEventsAbortController.signal,
      });
      this.#loadRecommendations();
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#cartEventsAbortController?.abort();
  }

  /**
   * Forces a recommendations refresh after cart AJAX updates in the drawer.
   */
  #handleCartChange = () => {
    // Let morph settle first, then reload against the new cart seed product
    requestAnimationFrame(() => {
      const live = document.querySelector('product-recommendations[data-cart-upsell="true"]');
      if (!(live instanceof ProductRecommendations) || !live.isConnected) return;

      const seedProductId =
        live.closest('cart-drawer-component')?.querySelector('[data-product-id]')?.getAttribute('data-product-id') ||
        live.dataset.productId;

      // Prefer the latest cart line product id from the drawer DOM if available
      const lastCartProduct = live
        .closest('cart-drawer-component')
        ?.querySelector('.cart-items [data-product-id], cart-items-component [data-product-id]');
      if (lastCartProduct instanceof HTMLElement && lastCartProduct.dataset.productId) {
        live.dataset.productId = lastCartProduct.dataset.productId;
      } else if (seedProductId) {
        live.dataset.productId = seedProductId;
      }

      live.dataset.recommendationsPerformed = 'false';
      live.classList.remove('hidden');
      live.#cachedRecommendations = {};
      live.#loadRecommendations();
    });
  };

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, fallbackIntent } = this.dataset;

    if (!productId || !sectionId) {
      // Empty cart / missing seed product — keep upsell hidden quietly
      if (this.dataset.cartUpsell === 'true') {
        this.classList.add('hidden');
        return;
      }
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchAndApplyRecommendations(productId, sectionId, intent, fallbackIntent);
  }

  /**
   * Fetches recommendations for the given intent and morphs them into the DOM. If a `fallbackIntent`
   * is provided and the first attempt returns no recommendations, retries once with the fallback
   * intent before giving up (e.g. falling back from 'complementary' to 'related').
   * @param {string} productId
   * @param {string} sectionId
   * @param {string | undefined} intent
   * @param {string | undefined} fallbackIntent
   */
  #fetchAndApplyRecommendations(productId, sectionId, intent, fallbackIntent) {
    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (!result.data?.trim().length) {
          this.#handleError(new Error('No recommendations available'));
          return;
        }

        if (fallbackIntent && fallbackIntent !== intent && this.#isEmptyResult(result.data)) {
          this.#fetchAndApplyRecommendations(productId, sectionId, fallbackIntent, undefined);
          return;
        }

        this.classList.remove('hidden');
        delete this.dataset.error;
        this.dataset.recommendationsPerformed = 'true';

        if (this.dataset.cartUpsell === 'true') {
          this.#applyCartUpsellMarkup(result.data);
          return;
        }

        morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Applies fetched cart-drawer upsell HTML into this element (no full-section morph).
   * @param {string} html
   */
  #applyCartUpsellMarkup(html) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const newUpsell =
      fragment.querySelector('product-recommendations[data-cart-upsell="true"]') ||
      fragment.querySelector('[data-hydration-key="cart-drawer-upsell"]') ||
      fragment.querySelector('product-recommendations');

    if (!newUpsell) {
      this.#handleError(new Error('Cart upsell markup missing from response'));
      return;
    }

    // Keep the host element instance; replace its contents + key attributes
    const nextProductId = newUpsell.getAttribute('data-product-id') || this.dataset.productId;
    if (nextProductId) this.dataset.productId = nextProductId;
    this.dataset.recommendationsPerformed = 'true';

    morph(this, newUpsell, { childrenOnly: true });

    if (!this.querySelector('.cart-drawer__upsell-item')) {
      this.classList.add('hidden');
    } else {
      this.classList.remove('hidden');
    }
  }

  /**
   * Checks whether the fetched section HTML has no rendered content for this element's hydration
   * target, meaning the requested intent returned zero recommendations.
   * @param {string} html - The fetched section HTML.
   * @returns {boolean}
   */
  #isEmptyResult(html) {
    const hydrationKey = this.getAttribute('data-hydration-key');
    const fragment = new DOMParser().parseFromString(html, 'text/html');

    if (this.dataset.cartUpsell === 'true') {
      return !fragment.querySelector('.cart-drawer__upsell-item');
    }

    if (!hydrationKey) return false;

    const target = fragment.querySelector(`[data-hydration-key="${hydrationKey}"]`);
    return !target || target.textContent.trim().length === 0;
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
